import { If } from "shared/components/control-statements";
import { CustomerFidelity } from "./oraculo/customers/CustomerDrawer/CustomerFidelity";
import { CustomerCoupons } from "./oraculo/customers/CustomerDrawer/CustomerCoupons";
import { useCallback, useEffect, useState } from "react";
import OcdpAPI from "providers/OcdpAPI";
import { EnvironmentStorage } from "shared/tools/EnvironmentStorage";
import {
  Button,
  Drawer,
  Empty,
  Label,
  Loading,
  Message,
  Steps,
  Tabs,
} from "@components";
import { Icon } from "shared/components/core/Icon";
import { use } from "react";
import { CustomerAPI } from "providers/CustomerAPI";
import { Phone } from "shared/utils/Phone";
import LazyInput from "shared/components/LazyInput";
import { Numbers } from "shared/utils/Numbers";
import OcdpAPI2 from "providers/OcdpAPI2";
import { Environment } from "shared/core/Environment";
import styles from "@styles/containers/cdp/customer-fidelity.module.scss";
import { CPF } from "shared/tools/CPF";
import InternalBenefitAPI from "providers/InternalBenefitAPI";
import ROLES from "data/Roles";
import { UserStorage } from "shared/tools/UserStorage";
import dayjs from "dayjs";

const tabs = {
  FIDELITY: {
    key: "FIDELITY",
    name: "Fidelidade",
  },
  COUPONS: {
    key: "COUPONS",
    name: "Cupons",
  },
};

const ocdp_api = new OcdpAPI2();

export default function BenefitManager({ visible, onClose }) {
  const [loading, setLoading] = useState(false);
  const [customer, setCustomer] = useState({});
  const [activeTab, setActiveTab] = useState(tabs.FIDELITY.key);
  const [currentStep, setCurrentStep] = useState(1);
  const [selectedCustomer, setSelectedCustomer] = useState({});
  const [block, setBlock] = useState(false);
  const [page_infos] = useState({
    page: null,
    execution_id: Environment.configurations?.ocdp_execution_id_v2,
    has_next: false,
    force: false,
    sort: null,
    limit: null,
  });

  const allowsBlockBenefit = [ROLES.SUPPORT, ROLES.ADMIN].includes(
    UserStorage.getLoggedUser()?.role,
  );

  const onLoad = async () => {
    setLoading(true);

    const internalBenefitAPI = new InternalBenefitAPI();
    const blockResponse = await internalBenefitAPI.getCustomerBlock(
      customer.id,
    );
    const block = blockResponse.getData(null);
    setBlock(block);

    setLoading(false);
  };

  useEffect(() => {
    if (customer) onLoad();
  }, [customer]);

  const environment = EnvironmentStorage.getEnvironment();
  const brandId = environment?.type === "UNIT" ? environment?.brand?.id : null;
  const unitId = environment?.type === "UNIT" ? environment?.id : null;

  const loadCustomer = useCallback(async () => {
    setLoading(true);

    const ocdp = new OcdpAPI();
    const customer = await ocdp.infos(selectedCustomer.id, {
      brand_id: null,
      unit_id: null,
    });

    setCustomer(customer);
    setLoading(false);
    setCurrentStep(2);
  }, [selectedCustomer.id]);

  const isPhoneNumber = (string) => {
    const normalizedInput = string.trim();
    const phoneRegex = /^(?:\(\d{2}\)\s?|\d{2}\s?)?\d{4,5}-?\d{4}$/;

    return phoneRegex.test(normalizedInput);
  };

  useEffect(() => {
    if (selectedCustomer.id) loadCustomer();
  }, [selectedCustomer, loadCustomer]);

  const loadCustomers = async (search) => {
    const { page, execution_id } = page_infos;

    const params = {
      brand_id: null,
      unit_id: null,
      page: page,
      size: 201,
      version: "V2",
    };

    params.term = search;

    if (isPhoneNumber(search) || (/^\d+$/.test(search) && search.length > 7)) {
      params.term = Numbers.onlyDigits(search);
    }

    let customers_page = {};

    customers_page = await ocdp_api.fetch(execution_id, params);

    const { page_items } = customers_page;

    return page_items.map((customer) => {
      const phone = Phone.format(customer.phone);

      return {
        id: customer.id,
        label: `${phone} ${customer.email ? `- ${customer.email}` : ""}`,
        name: customer.name ?? "Cliente não identificado",
      };
    });
  };

  const blockCustomer = async () => {
    const confirmed = await window.confirm({
      text: `Você tem certeza que deseja bloquear o uso de benefícios do cliente?`,
      confirmText: "Sim",
      cancelText: "Cancelar",
    });

    if (!confirmed) {
      return false;
    }

    setLoading(true);

    const internalBenefitAPI = new InternalBenefitAPI();
    const response = await internalBenefitAPI.blockCustomer(customer.id);

    if (response.getErrors([]).length > 0) {
      setLoading(false);
      return alert({ text: response.getErrors([])[0], buttonText: "Ok" });
    }

    await onLoad();
  };

  const unblockCustomer = async () => {
    const confirmed = await window.confirm({
      text: `Você tem certeza que deseja desbloquear o uso de benefícios do cliente?`,
      confirmText: "Sim",
      cancelText: "Cancelar",
    });

    if (!confirmed) {
      return false;
    }

    setLoading(true);

    const internalBenefitAPI = new InternalBenefitAPI();
    const response = await internalBenefitAPI.unblockCustomer(customer.id);

    if (response.getErrors([]).length > 0) {
      setLoading(false);
      return alert({ text: response.getErrors([])[0], buttonText: "Ok" });
    }

    await onLoad();
  };

  return (
    <Drawer
      visible={visible}
      onClose={() => {
        onClose();
        setCurrentStep(1);
      }}
      title="Gerenciador de benefícios"
      showCloseButton={false}
      subtitle={selectedCustomer?.name ?? ""}
      showBackButton
      backButton={() => {
        if (currentStep === 1) onClose();
        else setCurrentStep((prev) => prev - 1);
      }}
    >
      <If cond={currentStep === 1}>
        <If cond={loading}>
          <div className="d-flex align-center justify-center h-100">
            <Loading />
          </div>
        </If>

        <If cond={!loading}>
          <Label text="Insira o número ou nome do cliente abaixo:" />
          <LazyInput
            placeholder="Buscar por..."
            minTextLength={1}
            design="search"
            fillText={false}
            onOption={(option) => setSelectedCustomer(option)}
            findOptions={(term) => loadCustomers(term)}
          />

          <If cond={!selectedCustomer.id}>
            <Empty>
              <Icon name="gift" light size="2x" />
              <p>Nenhum cliente selecionado</p>
              <span>
                Você pode procurar o cliente por nome, e-mail ou número de
                telefone.
              </span>
            </Empty>
          </If>
        </If>
      </If>

      <If cond={currentStep === 2}>
        <Tabs
          activeTab={activeTab}
          onTabSelected={(i) => setActiveTab(i)}
          tabs={Object.values(tabs)}
          full
        />

        <div className={styles.selectedCustomer}>
          <p className={styles.name}>
            {customer.name ?? "Cliente nao identificado"}
          </p>
          <p className={styles.phone}>
            <If cond={customer.phone && !customer.doc}>
              {Phone.format(customer.phone)}
            </If>
            <If cond={customer.phone && customer.doc}>
              {Phone.format(customer.phone)} / {CPF.mask(customer.doc)}
            </If>
          </p>
        </div>

        {!block && allowsBlockBenefit && (
          <div className="mb-20 d-flex justify-center w-100">
            <Button design="primary-outline" onClick={blockCustomer}>
              Bloquear uso de benefício
            </Button>
          </div>
        )}

        {block && (
          <div className="mb-20 d-flex justify-center w-100">
            <Button design="primary-outline" onClick={unblockCustomer}>
              Desbloquear uso de benefício
            </Button>
          </div>
        )}

        <If cond={activeTab === tabs.FIDELITY?.key}>
          <CustomerFidelity
            customer={customer}
            brandId={brandId}
            unitId={unitId}
          />
        </If>
        <If cond={activeTab === tabs.COUPONS?.key}>
          <CustomerCoupons
            customer={customer}
            brandId={brandId}
            unitId={unitId}
          />
        </If>
      </If>
    </Drawer>
  );
}
